import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  EMPTY,
  Observable,
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  first,
  map,
  shareReplay,
  tap,
} from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { SortChangeEvent } from 'src/app/core/directives/sortable-header.directive';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { PagedList } from 'src/app/core/models/paged-list.interface';
import { Filter, RequestModel } from 'src/app/core/models/request-model.interface';
import { CompanyInvoice } from 'src/app/core/models/sales/company-invoice.interface';
import { CompanyInvoiceDetails } from 'src/app/core/models/sales/company-invoice-details.interface';
import {
  getInvoiceStatusLabelKey,
  InvoiceStatus,
  normalizeInvoiceStatus,
} from 'src/app/core/models/sales/invoice-status.type';
import { InvoiceService } from 'src/app/core/services/invoice.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { CreateInvoiceModalComponent } from './create-invoice-modal/create-invoice-modal.component';
import { InvoiceListItemView } from './invoice-list-item-view.interface';
import { InvoiceModalComponent } from './invoice-modal/invoice-modal.component';

interface InvoiceStatusFilterOption {
  value: InvoiceStatus | 'All';
  labelKey: string;
}

@Component({
  selector: 'app-invoices',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CoreModule,
    CreateInvoiceModalComponent,
    InvoiceModalComponent,
  ],
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.scss',
})
export class InvoicesComponent {
  private readonly invoiceService = inject(InvoiceService);
  private readonly permissionService = inject(PermissionService);
  private readonly translateService = inject(TranslateService);

  public readonly emptyListParam: Record<string, string> = {
    uk: 'рахунків',
    en: 'invoices',
  };

  public readonly statusFilterOptions: readonly InvoiceStatusFilterOption[] = [
    { value: 'All', labelKey: 'COMPONENTS.INVOICES.FILTERS.ALL' },
    { value: 'Open', labelKey: 'COMPONENTS.INVOICES.STATUSES.OPEN' },
    { value: 'Paid', labelKey: 'COMPONENTS.INVOICES.STATUSES.PAID' },
  ];

  public companyId = '';
  public isLoading = false;
  public isCreateModalOpen = false;
  public isDetailModalOpen = false;
  public selectedInvoiceId: string | null = null;
  public errorMessageKey = '';
  public searchTerm = '';
  public currentLang: 'uk' | 'en' = 'en';
  public statusFilter: InvoiceStatus | 'All' = 'All';

  public invoices$: Observable<PagedList<InvoiceListItemView>> = EMPTY;

  public readonly canCreate$ = this.permissionService.userPermissions$.pipe(
    map((permissions) =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Company,
        UserCompanyPermissions.Edit,
      ),
    ),
    shareReplay(1),
  );

  private readonly searchChanges$ = new Subject<string>();
  private request: RequestModel = {
    page: 1,
    pageSize: 10,
    filters: [],
    sortColumn: 'invoiceDate',
    sortOrder: 'desc',
  };

  constructor() {
    const route = inject(ActivatedRoute);

    route.parent?.parent?.params
      .pipe(
        first(),
        tap((params) => {
          this.companyId = params['id'];
          this.currentLang = this.translateService.currentLang === 'uk' ? 'uk' : 'en';
          this.isLoading = true;
          this.loadInvoices();
        }),
      )
      .subscribe();

    this.searchChanges$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((value) => {
          this.request.page = 1;
          this.request.searchTerm = value.trim() || undefined;
          this.isLoading = true;
          this.loadInvoices();
        }),
      )
      .subscribe();
  }

  public openCreateModal(): void {
    this.isCreateModalOpen = true;
  }

  public closeCreateModal(): void {
    this.isCreateModalOpen = false;
  }

  public onInvoiceCreated(invoice: CompanyInvoiceDetails): void {
    this.closeCreateModal();
    this.openDetailModal(invoice.id);
    this.isLoading = true;
    this.loadInvoices();
  }

  public openDetailModal(invoiceId: string): void {
    this.selectedInvoiceId = invoiceId;
    this.isDetailModalOpen = true;
  }

  public closeDetailModal(): void {
    this.isDetailModalOpen = false;
    this.selectedInvoiceId = null;
  }

  public onInvoicePaid(): void {
    this.closeDetailModal();
    this.isLoading = true;
    this.loadInvoices();
  }

  public onInvoiceDeleted(): void {
    this.closeDetailModal();
    this.isLoading = true;
    this.loadInvoices();
  }

  public retry(): void {
    this.isLoading = true;
    this.loadInvoices();
  }

  public onPageChange(page: number): void {
    this.request.page = page;
    this.loadInvoices();
  }

  public onChangePageSize(size: number): void {
    this.request.pageSize = size;
    this.request.page = 1;
    this.loadInvoices();
  }

  public onSearchChange(value: string): void {
    this.searchTerm = value;
    this.searchChanges$.next(value);
  }

  public onSortChange(event: SortChangeEvent): void {
    this.request.sortColumn = event.column;
    this.request.sortOrder = event.order;
    this.request.page = 1;
    this.isLoading = true;
    this.loadInvoices();
  }

  public onStatusFilterChange(option: InvoiceStatus | 'All'): void {
    if (this.statusFilter === option) {
      return;
    }
    this.statusFilter = option;
    this.request.page = 1;
    this.request.filters = this.buildFilters();
    this.isLoading = true;
    this.loadInvoices();
  }

  public get activeSortColumn(): string {
    return this.request.sortColumn ?? '';
  }

  public get activeSortOrder(): 'asc' | 'desc' {
    return this.request.sortOrder ?? 'asc';
  }

  private buildFilters(): Filter[] {
    if (this.statusFilter === 'All') {
      return [];
    }
    return [{ field: 'status', operator: 'eq', value: this.statusFilter, values: [] }];
  }

  private loadInvoices(): void {
    if (!this.companyId) {
      return;
    }

    this.errorMessageKey = '';
    this.invoices$ = this.invoiceService
      .getCompanyInvoicesPaged(this.companyId, this.request)
      .pipe(
        map((page) => ({
          ...page,
          items: page.items.map((invoice) => this.mapInvoiceToView(invoice)),
        })),
        tap(() => {
          this.isLoading = false;
        }),
        catchError(() => {
          this.errorMessageKey = 'COMPONENTS.INVOICES.ERRORS.LOAD_FAILED';
          this.isLoading = false;
          return EMPTY;
        }),
        shareReplay(1),
      );
  }

  private mapInvoiceToView(invoice: CompanyInvoice): InvoiceListItemView {
    const status = normalizeInvoiceStatus(invoice.status);
    return {
      id: invoice.id,
      number: invoice.number || '—',
      customerName: invoice.customerName?.trim() || '—',
      salesOrderNumber: invoice.salesOrderNumber?.trim() || '—',
      invoiceDate: invoice.invoiceDate ?? '',
      dueDate: invoice.dueDate ?? '',
      status,
      statusLabelKey: getInvoiceStatusLabelKey(status),
      statusBadgeClass: InvoicesComponent.statusBadgeClass(status),
      total: invoice.total,
      hasLink: !!invoice.salesOrderId,
    };
  }

  private static statusBadgeClass(status: InvoiceStatus): string {
    return status === 'Paid'
      ? 'invoices-page__status-badge--paid'
      : 'invoices-page__status-badge--open';
  }
}
